import DriveSidebar from 'components/layout/DriveSidebar'
import Panel, { PanelHeading } from 'components/layout/Panel'
import { Breadcrumb } from 'components/misc/Breadcrumb'
import React, { useCallback, useEffect, useState } from 'react'
import Helmet from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { Container, Form, RoadieApiResponse, Spacer, Text } from 'roadie-ui'
import Page from 'rui/widgets/Page'
import { initialModalOptions } from 'types/global'
import { SubmittedModal } from '../SubmittedModal'
import { getSalesforceCases, postSalesforceCase } from 'actions/salesforce'
import { getLastUpdatedOpenCase, SalesforceCase } from 'util/salesforce'
import { Redirect } from 'react-router-dom'
import { useLocalization, LocalizedText } from 'localization'
import type { TranslationKey } from 'localization'

const IDVHelpFormPage: React.FC = () => {
  const { t } = useLocalization()
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)
  const [modalOptions, setModalOptions] = useState(initialModalOptions)
  const [idvHelpSummary, setIdvHelpSummary] = useState<any>(null)

  const dispatch = useDispatch()

  const handleSubmit = useCallback(
    async (formData: any) => {
      try {
        const payload = {
          case_type: 'idv',
          rich_description: formData.description,
        }
        await postSalesforceCase(payload).then(() => {
          setModalOptions({ modalOpen: true, modalType: 'idvHelpSubmitted' })
        })
      } catch (error: any) {
        let messageKey: TranslationKey = 'dweb_flash_idv_help_submit_failed_try_again_later'
        if (error.response) {
          const status = error.response.rawResponse?.status
          if (status === 422) {
            messageKey = 'dweb_flash_idv_help_already_submitted'
          } else if (status === 400) {
            messageKey = 'dweb_flash_idv_help_account_not_eligible'
          }
        }
        dispatch({
          type: 'PUSH_FLASH',
          flash: { messageKey, type: 'error' },
        })
      }
    },
    [dispatch]
  )

  useEffect(() => {
    getSalesforceCases().then((data: RoadieApiResponse) => {
      const mostRecentAppeal: SalesforceCase | null = getLastUpdatedOpenCase(data, 'idv')
      setIdvHelpSummary(mostRecentAppeal || null)
    })
  }, [])

  // Redirect to /account/idv-help-status if there is an open case
  if (idvHelpSummary) {
    return <Redirect to="/account/idv-help-status" />
  }

  return (
    <Page sidebar={<DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_dashboard_salesforce_idv_idvhelp_form_request_id_verification_help')}</title>
      </Helmet>
      {!isWebView && (
        <Breadcrumb
          items={[
            { label: t('dweb_common_my_profile'), linkTo: '/dashboard' },
            { label: t('dweb_dashboard_salesforce_idv_idvhelp_form_request_id_verification_help') },
          ]}
        />
      )}
      <Panel>
        <Container.Base $padding="2.4rem 0">
          {!isWebView && (
            <PanelHeading>
              <LocalizedText translationKey="dweb_dashboard_salesforce_idv_idvhelp_form_request_id_verification_help" />
            </PanelHeading>
          )}
          <Spacer $height="2.4rem" />
          <Text.Body1 $color="neutral-black">
            <LocalizedText translationKey="dweb_idv_help_form_description_prompt" />
          </Text.Body1>
          <Spacer $height="1.6rem" />
          <Form.Basic
            id="idv-help-form"
            onSubmit={handleSubmit}
            validations={[['description', Form.validations.required]]}
          >
            <Form.Input
              label={t('dweb_dashboard_salesforce_idv_idvhelp_form_describe_issue')}
              name="description"
              maxLength={500}
            />
            <Spacer $height="2.4rem" />
            <Form.Submit>
              <LocalizedText translationKey="dweb_common_submit" />
            </Form.Submit>
          </Form.Basic>
        </Container.Base>
      </Panel>

      <SubmittedModal
        modalOptions={modalOptions}
        isWebView={isWebView}
        linkTo={'idv-help-status'}
        modalDescription={t('dweb_dashboard_salesforce_idv_idvhelp_form_help_request_submitted_confirmation')}
      />
    </Page>
  )
}

export default IDVHelpFormPage
